import Modal from '@mui/material/Modal';
import * as React from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { connect } from 'react-redux';
import { useOpenReportingLocation } from 'src/hooks';
import { diff } from 'src/utils/time';
import {
  getExpiry as getExpiryOpen,
  touch as touchOpen,
} from '../creators/OpenReporting.creators';
import { getExpiry, touch } from '../creators/User.creators';
import '../css/ActivityMonitor.css';
import * as Types from '../types';
import { Button } from './atoms';

interface Props {
  authenticated: boolean;
  getExpiry: () => Promise<string>;
  getExpiryOpen: () => Promise<string>;
  touch: () => Promise<unknown>;
  touchOpen: () => Promise<unknown>;
}

export const ActivityMonitor: React.FC<Props> = (props) => {
  // The backend session expiry time in ms.
  const TIMEOUT = 60 * 60 * 1000;
  // For how many ms before session expiry should we show the modal.
  const GRACE = 2 * 60 * 1000;
  // While the user is active, update the session at most this often.
  const THROTTLE = 30 * 1000;

  const [open, setOpen] = React.useState(false);
  const [sent, setSent] = React.useState(new Date());
  const { isOpenReporting } = useOpenReportingLocation();

  const touchAction = isOpenReporting ? props.touchOpen : props.touch;
  const getExpiryAction = isOpenReporting
    ? props.getExpiryOpen
    : props.getExpiry;

  const handleOnIdle = async () => {
    if (!props.authenticated) {
      setOpen(false);
      return;
    }

    const updatedAt = await getExpiryAction();
    const remaining = TIMEOUT - diff(new Date(), updatedAt);

    if (remaining > 0 && remaining < GRACE) {
      setOpen(true);
      setTimeout(handleOnIdle, remaining);
    } else {
      setOpen(false);
      setTimeout(handleOnIdle, remaining - GRACE);
    }
  };

  const handleOnAction = async () => {
    const due = diff(new Date(), sent) > THROTTLE;

    if (due && !open && props.authenticated) {
      setSent(new Date());
      await touchAction();
    }
  };

  useIdleTimer({
    timeout: TIMEOUT - GRACE,
    onIdle: handleOnIdle,
    onAction: handleOnAction,
    throttle: THROTTLE,
    events: ['mousedown'],
  });

  const onClick = async () => {
    setSent(new Date());
    await touchAction();
    setOpen(false);
  };

  return (
    <div>
      <Modal
        open={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div>
          <div className="activity-modal">
            <div className={'activity-text'}>
              <strong>
                {isOpenReporting
                  ? 'Are you still there?'
                  : 'Still using Vault Resolution Hub?'}
              </strong>
            </div>
            <div className={'activity-text'}>
              {'For security, you will be automatically logged out shortly.'}
            </div>
            <Button variant="primary" size="medium" onClick={onClick}>
              Continue session
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: Types.State) => {
  const session = state?.globalUsers?.session;
  const openReportingSession =
    state?.openReporting?.openReportingReducer?.session;
  return {
    authenticated: Boolean(session || openReportingSession),
  };
};

export default connect(mapStateToProps, {
  getExpiry,
  touch,
  touchOpen,
  getExpiryOpen,
})(ActivityMonitor);
